import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff, Plus, X, Save } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const EMPTY = {
  name: "",
  rating: 5,
  text: "",
  date: "",
  source: "google",
  visible: true,
  order: 0,
};

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`rating-star-${n}`}
          onClick={() => onChange(n)}
          className="p-1"
          aria-label={`${n} Sterne`}
        >
          <Star
            size={20}
            strokeWidth={1.5}
            className={
              n <= value
                ? "fill-[hsl(var(--brand-gold))] text-[hsl(var(--brand-gold))]"
                : "text-[hsl(var(--muted-foreground))]/40"
            }
          />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error("Bitte Name und Text angeben.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/reviews/${form.id}`, {
          name: form.name,
          rating: form.rating,
          text: form.text,
          date: form.date,
          source: form.source,
          visible: form.visible,
          order: Number(form.order) || 0,
        });
        toast.success("Rezension aktualisiert");
      } else {
        await api.post("/admin/reviews", {
          name: form.name,
          rating: form.rating,
          text: form.text,
          date: form.date,
          source: form.source,
          visible: form.visible,
          order: Number(form.order) || 0,
        });
        toast.success("Rezension gespeichert");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form
      onSubmit={submit}
      data-testid="review-form"
      className="border border-border bg-[hsl(var(--card))] p-6 rounded-sm space-y-4"
    >
      <div className="flex items-start justify-between">
        <h3 className="font-display text-xl">{isEdit ? "Rezension bearbeiten" : "Neue Rezension"}</h3>
        <button type="button" onClick={onCancel} className="p-1 hover:bg-[hsl(var(--secondary))] rounded-sm" aria-label="Schließen">
          <X size={16} />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block text-sm">
          <span className="eyebrow block mb-1">Name des Gastes</span>
          <input
            data-testid="review-input-name"
            required
            value={form.name}
            onChange={(e) => upd("name", e.target.value)}
            className="w-full px-3 py-2 border border-border bg-white"
          />
        </label>
        <label className="block text-sm">
          <span className="eyebrow block mb-1">Datum (optional, z. B. „Sept. 2025")</span>
          <input
            data-testid="review-input-date"
            value={form.date || ""}
            onChange={(e) => upd("date", e.target.value)}
            className="w-full px-3 py-2 border border-border bg-white"
          />
        </label>
      </div>

      <div>
        <span className="eyebrow block mb-2">Sternebewertung</span>
        <StarPicker value={form.rating} onChange={(v) => upd("rating", v)} />
      </div>

      <label className="block text-sm">
        <span className="eyebrow block mb-1">Text der Rezension</span>
        <textarea
          data-testid="review-input-text"
          required
          rows={5}
          value={form.text}
          onChange={(e) => upd("text", e.target.value)}
          className="w-full px-3 py-2 border border-border bg-white leading-relaxed"
        />
      </label>

      <div className="grid md:grid-cols-3 gap-4 items-end">
        <label className="block text-sm">
          <span className="eyebrow block mb-1">Quelle</span>
          <input
            data-testid="review-input-source"
            value={form.source || "google"}
            onChange={(e) => upd("source", e.target.value)}
            className="w-full px-3 py-2 border border-border bg-white"
          />
        </label>
        <label className="block text-sm">
          <span className="eyebrow block mb-1">Reihenfolge (niedriger = weiter oben)</span>
          <input
            data-testid="review-input-order"
            type="number"
            value={form.order ?? 0}
            onChange={(e) => upd("order", e.target.value)}
            className="w-full px-3 py-2 border border-border bg-white"
          />
        </label>
        <label className="inline-flex items-center gap-2 text-sm mt-2">
          <input
            data-testid="review-input-visible"
            type="checkbox"
            checked={!!form.visible}
            onChange={(e) => upd("visible", e.target.checked)}
          />
          <span>Auf Website anzeigen</span>
        </label>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          data-testid="review-save-btn"
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill disabled:opacity-50"
        >
          <Save size={14} /> {isEdit ? "Speichern" : "Anlegen"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-semibold border border-border hover:bg-[hsl(var(--secondary))] rounded-sm"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // review object or null
  const [creating, setCreating] = useState(false);
  const [settings, setSettings] = useState({ google_reviews_url: "", google_write_review_url: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rev }, { data: st }] = await Promise.all([
        api.get("/admin/reviews"),
        api.get("/admin/settings"),
      ]);
      setItems(rev || []);
      setSettings({
        google_reviews_url: st?.google_reviews_url || "",
        google_write_review_url: st?.google_write_review_url || "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleVisible = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { visible: !r.visible });
      setItems((arr) => arr.map((it) => (it.id === r.id ? { ...it, visible: !r.visible } : it)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Rezension von ${r.name} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      setItems((arr) => arr.filter((it) => it.id !== r.id));
      toast.success("Rezension gelöscht");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const { data } = await api.put("/admin/settings", settings);
      setSettings({
        google_reviews_url: data?.google_reviews_url || "",
        google_write_review_url: data?.google_write_review_url || "",
      });
      toast.success("Google-Links gespeichert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div data-testid="admin-reviews" className="space-y-10">
      {/* Settings */}
      <section>
        <h2 className="font-display text-2xl md:text-3xl mb-4">Google-Links</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 max-w-3xl">
          Diese Links werden auf der Startseite unter den Rezensionen angezeigt. Solange ein Feld leer bleibt, wird der entsprechende Button ausgeblendet.
        </p>
        <form onSubmit={saveSettings} className="grid md:grid-cols-2 gap-4 max-w-4xl" data-testid="settings-form">
          <label className="block text-sm">
            <span className="eyebrow block mb-1">„Alle Bewertungen auf Google ansehen"</span>
            <input
              data-testid="settings-view-url"
              value={settings.google_reviews_url}
              onChange={(e) => setSettings((s) => ({ ...s, google_reviews_url: e.target.value }))}
              placeholder="https://…"
              className="w-full px-3 py-2 border border-border bg-white"
            />
          </label>
          <label className="block text-sm">
            <span className="eyebrow block mb-1">„Tæste auf Google bewerten"</span>
            <input
              data-testid="settings-write-url"
              value={settings.google_write_review_url}
              onChange={(e) => setSettings((s) => ({ ...s, google_write_review_url: e.target.value }))}
              placeholder="https://…"
              className="w-full px-3 py-2 border border-border bg-white"
            />
          </label>
          <div>
            <button
              type="submit"
              data-testid="settings-save-btn"
              disabled={savingSettings}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill disabled:opacity-50"
            >
              <Save size={14} /> Speichern
            </button>
          </div>
        </form>
      </section>

      {/* Reviews list */}
      <section>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-2xl md:text-3xl">Rezensionen</h2>
          {!creating && !editing && (
            <button
              type="button"
              data-testid="review-add-btn"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill"
            >
              <Plus size={14} /> Neue Rezension
            </button>
          )}
        </div>

        {creating && (
          <div className="mb-6">
            <ReviewForm
              onCancel={() => setCreating(false)}
              onSaved={() => {
                setCreating(false);
                load();
              }}
            />
          </div>
        )}
        {editing && (
          <div className="mb-6">
            <ReviewForm
              initial={editing}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          </div>
        )}

        {loading ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))]">…</p>
        ) : items.length === 0 ? (
          <div className="border border-border p-10 text-center text-[hsl(var(--muted-foreground))]">
            Noch keine Rezensionen. Klicke oben auf „Neue Rezension".
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <article
                key={r.id}
                data-testid={`admin-review-${r.id}`}
                className="border border-border bg-[hsl(var(--card))] p-5 md:p-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-semibold">{r.name}</span>
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={14}
                          strokeWidth={1.5}
                          className={
                            n <= r.rating
                              ? "fill-[hsl(var(--brand-gold))] text-[hsl(var(--brand-gold))]"
                              : "text-[hsl(var(--muted-foreground))]/40"
                          }
                        />
                      ))}
                    </span>
                    {r.date && (
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{r.date}</span>
                    )}
                    <span
                      className={`text-xs px-2 py-0.5 rounded-sm ${
                        r.visible
                          ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                          : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"
                      }`}
                    >
                      {r.visible ? "sichtbar" : "ausgeblendet"}
                    </span>
                    <span className="text-xs text-[hsl(var(--muted-foreground))]">
                      Order: {r.order ?? 0}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{r.text}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`review-toggle-${r.id}`}
                    onClick={() => toggleVisible(r)}
                    className="p-2 hover:bg-[hsl(var(--secondary))] rounded-sm"
                    aria-label={r.visible ? "Ausblenden" : "Anzeigen"}
                    title={r.visible ? "Ausblenden" : "Anzeigen"}
                  >
                    {r.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button
                    data-testid={`review-edit-${r.id}`}
                    onClick={() => setEditing(r)}
                    className="px-3 py-2 text-xs tracking-[0.22em] uppercase font-semibold border border-border hover:bg-[hsl(var(--secondary))] rounded-sm"
                  >
                    Bearbeiten
                  </button>
                  <button
                    data-testid={`review-delete-${r.id}`}
                    onClick={() => remove(r)}
                    className="p-2 hover:bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] rounded-sm"
                    aria-label="Löschen"
                    title="Löschen"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
